/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ActionModel } from "../../Common/ActionModel";
import { Panel, PanelNote } from "../../../../ui/components/Panel";
import { T } from "@adapptio/lib-components";
import { termsEditorActionEditor } from "../../../../terms/actionEditor";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { TActionEditSelectionItem } from "../../Common/ActionEditSelection";
import { ActionEditorView } from "../ActionEditorView";
import { InspectActionSelectionItem } from "../../Common/InspectActionSelectionItem";
import {
	EditorInspectorContext,
	IEditorInspectorContext
} from "../../../../ui/contexts/EditorInspectorContext";
import { REFERENCE_TYPE } from "../../../Core/ReferenceGuide/ReferenceGuideTypes";

function getSelectedItemOrEvent(model: ActionModel): TActionEditSelectionItem {
	const selection = model.getSelection();
	const selectedItems = selection.getItems();

	if (selectedItems?.length !== 1) {
		return null;
	}

	return selectedItems[0];
}

export interface IActionEditorInspectorProps {
	view: ActionEditorView;
}

export const ActionEditorInspector: React.FunctionComponent<IActionEditorInspectorProps> = ({ view }) => {
	const model = view.getActionModel();

	const editSelection = useMemo(() => model.getSelection(), [ model ]);
	const [ areMultipleItemsSelected, setAreMultipleItemsSelected ] = useState(
		() => editSelection.getItems().length > 1
	);

	const [ selectedItemOrEvent, setSelectedItemOrEvent ] = useState<TActionEditSelectionItem>(() =>
		getSelectedItemOrEvent(model)
	);

	const refreshSelectedItemOrEvent = useCallback(() => {
		const item = getSelectedItemOrEvent(model);
		setSelectedItemOrEvent(item ? { ...item } : item);
	}, [ model, setSelectedItemOrEvent ]);

	const getNodeById = useCallback(
		(nodeId: number) => model.getDesignContext().getNodeById(nodeId),
		[ model ]
	);

	const selectedModel = useMemo(
		() => getNodeById(selectedItemOrEvent?.nodeId) || null,
		[ selectedItemOrEvent, getNodeById ]
	);

	const inspectorCtx = useMemo<IEditorInspectorContext>(() => {
		return {
			referenceItem: {
				category: REFERENCE_TYPE.ACTIONS,
				type: REFERENCE_TYPE.ACTIONS,
				name: selectedModel?.["type"] || null
			}
		};
	}, [ selectedModel ]);

	useEffect(() => {
		const handleModelChange = () => {
			refreshSelectedItemOrEvent();
		};

		const handleSelectionChange = () => {
			setAreMultipleItemsSelected(editSelection.getItems().length > 1);
			refreshSelectedItemOrEvent();
		};

		onEvent(model.onModelChange, handleModelChange);
		onEvent(editSelection.onChange, handleSelectionChange);

		return () => {
			offEvent(model.onModelChange, handleModelChange);
			offEvent(editSelection.onChange, handleSelectionChange);
		};
	}, [ model, editSelection ]);

	return (
		<Panel scroll={true}>
			<EditorInspectorContext.Provider value={inspectorCtx}>
				{
					<InspectActionSelectionItem
						selectionItem={selectedItemOrEvent}
						getNodeById={getNodeById}
						view={view}
					/>
				}
				{areMultipleItemsSelected ? (
					<PanelNote>
						<p>
							<T
								domain="editor"
								term={termsEditorActionEditor.inspector.cannotEditMultipleNodes}
							/>
						</p>
					</PanelNote>
				) : null}
				{!selectedModel && !areMultipleItemsSelected ? (
					<PanelNote>
						<p>
							<T domain="editor" term={termsEditorActionEditor.inspector.selectNodeToInspect} />
						</p>
					</PanelNote>
				) : null}
			</EditorInspectorContext.Provider>
		</Panel>
	);
};
